import { useState } from 'react';
import type { useTranslation } from 'react-i18next';
import type { useProfile } from 'app/network/profile';
import { useJoinPlace, useLeavePlace, useMyPlaces, useSharingPlaceMembers } from 'app/network/sharing-place';
import { ChannelType, SUPPORT_CHANNEL_URL, useCreateChannel } from 'app/network/chat/channel';
import { useReportUser } from 'app/network/user';
import { navigateToChat } from 'app/screens/chat/navigator-helper';
import { getTodayOpeningStatus } from 'app/lib/opening-hours';
import { contactProfile, getNextOpenDay, hasPlaceDetails, isUserFrequentingPlace } from './shared';

export function usePlaceProfileState({
  isLoggedIn,
  isMyself,
  profile,
  t,
  userId,
}: {
  isLoggedIn: boolean;
  isMyself: boolean;
  profile: ReturnType<typeof useProfile>['data'];
  t: ReturnType<typeof useTranslation>['t'];
  userId: string;
}) {
  const candidatePlaceDetails = profile?.placeDetails;
  const isPlace = hasPlaceDetails(candidatePlaceDetails);
  const placeDetails = isPlace ? candidatePlaceDetails : undefined;
  const websiteUrl = placeDetails?.websiteUrl?.trim();
  const hasWebsite = Boolean(websiteUrl);
  const openingStatus = getTodayOpeningStatus(placeDetails?.openingHours);
  const nextOpenDay = getNextOpenDay({ isPlace, openingHours: placeDetails?.openingHours, openingStatus, t });
  const myPlacesQuery = useMyPlaces({ enabled: isLoggedIn && isPlace && !isMyself });
  const isFrequenting = isUserFrequentingPlace({ isPlace, placeUserId: userId, places: myPlacesQuery.data });
  const membersQuery = useSharingPlaceMembers(isPlace ? userId : undefined);
  const placeMembers = membersQuery.data ?? [];
  const [showOpeningHours, setShowOpeningHours] = useState(false);
  const [showJoinConfirm, setShowJoinConfirm] = useState(false);
  const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);
  const [showPlaceHelp, setShowPlaceHelp] = useState(false);
  const [showDiscount, setShowDiscount] = useState(false);
  const [showPlaceMembers, setShowPlaceMembers] = useState(false);
  const { mutate: joinPlace, isPending: isJoining } = useJoinPlace({
    onSuccess: () => setShowJoinConfirm(false),
  });
  const { mutate: leavePlace, isPending: isLeaving } = useLeavePlace({
    onSuccess: () => setShowLeaveConfirm(false),
  });

  return {
    hasWebsite,
    isFrequenting,
    isJoining,
    isLeaving,
    isPlace,
    joinPlace,
    leavePlace,
    nextOpenDay,
    openingStatus,
    placeDetails,
    placeMembers,
    setShowDiscount,
    setShowJoinConfirm,
    setShowLeaveConfirm,
    setShowOpeningHours,
    setShowPlaceHelp,
    setShowPlaceMembers,
    showDiscount,
    showJoinConfirm,
    showLeaveConfirm,
    showOpeningHours,
    showPlaceHelp,
    showPlaceMembers,
    websiteUrl,
  };
}

function reportProfileUser(
  reportUser: ReturnType<typeof useReportUser>['mutate'],
  userId: string,
  navigation: Parameters<typeof navigateToChat>[0],
) {
  reportUser(userId, {
    onSuccess: () => {
      navigateToChat(navigation, SUPPORT_CHANNEL_URL, {
        i18nKey: 'reportUserPrompt',
        ns: 'neighbour-profile',
      });
    },
  });
}

export function useProfileActions({
  authorize,
  navigation,
  profile,
  route,
}: Pick<Parameters<typeof contactProfile>[0], 'authorize' | 'navigation' | 'profile' | 'route'>) {
  const { mutate: reportUser, isPending: isReportingUser } = useReportUser();
  const [showReportConfirmation, setShowReportConfirmation] = useState(false);
  const [isImageViewerModalOpen, setIsImageViewerModalOpen] = useState(false);
  const [isAmbassadorSheetOpen, setIsAmbassadorSheetOpen] = useState(false);
  const {
    error: channelMutationError,
    mutate: mutateChannel,
    isPending: isContactPending,
  } = useCreateChannel(route.params.listingId ? ChannelType.Listing : ChannelType.Member, {
    onSuccess: (channel) => navigateToChat(navigation, channel.channel_url),
  });

  const contactButtonHandler = () => {
    contactProfile({ authorize, mutateChannel, navigation, profile, route });
  };
  const handleReportUser = () => {
    setShowReportConfirmation(false);
    reportProfileUser(reportUser, route.params.userId, navigation);
  };

  return {
    channelMutationError,
    contactButtonHandler,
    handleReportUser,
    isAmbassadorSheetOpen,
    isContactPending,
    isImageViewerModalOpen,
    isReportingUser,
    setIsAmbassadorSheetOpen,
    setIsImageViewerModalOpen,
    setShowReportConfirmation,
    showReportConfirmation,
  };
}
